import { ArrowDownward, ArrowUpward, ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  List,
  Stack,
  Typography,
} from '@mui/material';
import EntityView from 'components/entityView';
import _ from 'lodash';
import { plural } from 'pluralize';
import React, { useCallback, useMemo, useState } from 'react';
import {
  FinsightEntity,
  FinsightEntityField,
  getEntityExtraFieldValue,
} from 'shared';
import { ControllerState } from 'types/react';
import { formatName, sortEntities } from 'utils/fields';

import DroppableList from './droppableList';

type NestedEntityGroup = {
  key: string;
  groupValue: unknown;
  groupFriendlyValue: string;
  groupField: string;
  entities: FinsightEntity[];
  subGroups?: NestedEntityGroup[];
};

export function ProjectsList(props: {
  entities?: FinsightEntity[];
  entityViewProps?: Partial<React.ComponentProps<typeof EntityView>>;
  hideType?: boolean;
  categorizeBy?: ControllerState<string[]>;
  sortBy?: ControllerState<string | null>;
}) {
  const {
    entityViewProps,
    hideType: _hideType,
    categorizeBy,
    sortBy,
    entities,
  } = props;
  const hideType = _hideType ?? true;

  const [hiddenGroups, setHiddenGroups] = useState<Set<string>>(new Set());
  const [sortAsc, setSortAsc] = useState<boolean>(false);
  const [sortCatAsc, setSortCatAsc] = useState<boolean>(false);

  const groupFields = useMemo(() => {
    if (categorizeBy?.current && categorizeBy.current.length > 0) {
      return categorizeBy.current;
    }

    // Auto-detect groupable field if no categorization is selected
    let selectedField: FinsightEntityField['schema'] | null = null as any;

    for (const entity of entities || []) {
      for (const [name, field] of Object.entries(entity.extraFieldsMeta)) {
        if (field.schema.groupable) {
          if (selectedField?.name !== name) {
            if ((field.schema.order ?? 0) > (selectedField?.order ?? -1)) {
              selectedField = {
                name,
                ...field.schema,
              };
            }
          }
        }
      }
    }

    return selectedField?.name ? [selectedField.name] : [];
  }, [categorizeBy, entities]);

  const getGroupValue = (
    entity: FinsightEntity,
    fieldName: string,
  ): { value: unknown; friendlyValue: string } => {
    if (fieldName === 'type') {
      return {
        value: entity.types[0],
        friendlyValue: plural(formatName(entity.types[0])),
      };
    } else if (fieldName in entity.extraFields) {
      try {
        const newValue = getEntityExtraFieldValue(
          entity.extraFieldsMeta[fieldName],
          entity.extraFields[fieldName],
        );
        const newFriendlyValue = getEntityExtraFieldValue(
          entity.extraFieldsMeta[fieldName],
          entity.extraFields[fieldName],
          true,
        );

        const newFriendlyValueStr = String(newFriendlyValue ?? newValue);

        return {
          value: newValue,
          friendlyValue: newFriendlyValueStr,
        };
      } catch {}
    }
    return { value: '', friendlyValue: '' };
  };

  const sortGroup = useCallback(
    (groups: NestedEntityGroup[], fieldName: string): NestedEntityGroup[] => {
      return groups.sort((_a, _b) => {
        const a = sortCatAsc ? _a : _b;
        const b = sortCatAsc ? _b : _a;

        if (
          fieldName !== 'type' &&
          a.entities.length > 0 &&
          b.entities.length > 0
        ) {
          const groupableField =
            a.entities[0].extraFieldsMeta[fieldName] ??
            b.entities[0].extraFieldsMeta[fieldName];

          if (groupableField?.schema.type === 'enum') {
            let aValue: unknown = '';
            let bValue: unknown = '';

            try {
              aValue = getEntityExtraFieldValue(groupableField, a.groupValue);
            } catch {}

            try {
              bValue = getEntityExtraFieldValue(groupableField, b.groupValue);
            } catch {}

            if (Array.isArray(groupableField.schema.properties)) {
              let aOrder = 0;
              let bOrder = 0;
              if (typeof aValue === 'number') {
                aOrder = groupableField.schema.properties[aValue].order ?? 0;
              }
              if (typeof bValue === 'number') {
                bOrder = groupableField.schema.properties[bValue].order ?? 0;
              }
              if (aOrder !== bOrder) {
                return aOrder - bOrder;
              }
            }
          }
        }
        return a.groupFriendlyValue.localeCompare(b.groupFriendlyValue);
      });
    },
    [sortCatAsc],
  );

  const createNestedGroups = useCallback(
    (
      entities: FinsightEntity[],
      fields: string[],
      depth: number = 0,
    ): NestedEntityGroup[] => {
      if (fields.length === 0 || depth >= fields.length) {
        // No more grouping, return flat list
        return [];
      }

      const currentField = fields[depth];
      const groupsMap: Record<string, NestedEntityGroup> = {};

      for (const entity of entities) {
        const { value, friendlyValue } = getGroupValue(entity, currentField);
        const key =
          value !== undefined && value !== null
            ? `${currentField !== 'type' ? `${entity.types[0]}.` : ''}${value}`
            : '';

        const uniqueKey = depth === 0 ? key : `${currentField}-${depth}-${key}`;
        if (!groupsMap[key]) {
          groupsMap[key] = {
            key: uniqueKey,
            groupValue: value,
            groupFriendlyValue: friendlyValue,
            groupField: currentField,
            entities: [],
            subGroups: [],
          };
        }

        groupsMap[key].entities.push(entity);
      }

      const groupsArray = Object.values(groupsMap);

      // Sort groups
      const sortedGroups = sortGroup(groupsArray, currentField);

      // If there are more fields, create nested groups
      if (depth < fields.length - 1) {
        for (const group of sortedGroups) {
          group.subGroups = createNestedGroups(
            group.entities,
            fields,
            depth + 1,
          );
        }
      } else {
        // Last level, sort entities within each group
        sortedGroups.forEach((group) => {
          group.entities.sort((a, b) =>
            sortEntities(a, b, {
              overrideFieldNames: sortBy?.current
                ? [sortBy.current]
                : undefined,
              sortAsc,
            }),
          );
        });
      }

      return sortedGroups;
    },
    [sortGroup, sortBy, sortAsc],
  );

  const entityGroups = useMemo<NestedEntityGroup[]>(() => {
    if (groupFields.length === 0) {
      // No grouping, return flat structure
      return [];
    }

    return createNestedGroups(entities || [], groupFields, 0);
  }, [createNestedGroups, entities, groupFields]);

  const renderGroup = (
    group: NestedEntityGroup,
    allEntities: FinsightEntity[],
    depth: number = 0,
  ): React.ReactNode => {
    const hasSubGroups = group.subGroups && group.subGroups.length > 0;
    const shouldShowHeader =
      group.groupFriendlyValue !== '' || (hasSubGroups && depth === 0);

    if (hasSubGroups) {
      return (
        <Accordion
          variant="outlined"
          key={group.key}
          expanded={!hiddenGroups.has(group.key)}
          disableGutters
          onChange={() => {
            setHiddenGroups((prev) => {
              const newSet = new Set(prev);
              if (newSet.has(group.key)) {
                newSet.delete(group.key);
              } else {
                newSet.add(group.key);
              }
              return newSet;
            });
          }}
          sx={{
            border: 0,
            boxShadow: 'none',
            marginBottom: 1,
            marginLeft: depth > 0 ? 2 : 0,
            '& .MuiAccordionDetails-root': {
              padding: 0,
            },
            '& .MuiAccordionSummary-content': {
              margin: 0,
            },
            '& .MuiPaper-root': {
              boxShadow: 'none',
            },
          }}
        >
          {shouldShowHeader && (
            <AccordionSummary
              sx={{
                minHeight: 48,
                '&.Mui-expanded': {
                  minHeight: 48,
                },
                '& .MuiAccordionSummary-content': {
                  marginY: 1,
                },
              }}
              expandIcon={<ExpandMore />}
            >
              <Typography
                variant="subtitle1"
                fontWeight={600}
                color="text.primary"
                textTransform="capitalize"
              >
                {group.groupFriendlyValue
                  ? `${formatName(group.groupField)}: ${group.groupFriendlyValue}`
                  : 'Ungrouped'}
                {!hiddenGroups.has(group.key)
                  ? ''
                  : ` (${group.entities.length})`}
              </Typography>
            </AccordionSummary>
          )}
          <AccordionDetails>
            {group.subGroups!.map((subGroup) =>
              renderGroup(subGroup, allEntities, depth + 1),
            )}
          </AccordionDetails>
        </Accordion>
      );
    }

    // Leaf group - render entities
    return (
      <Accordion
        variant="outlined"
        key={group.key}
        expanded={!hiddenGroups.has(group.key)}
        disableGutters
        onChange={() => {
          setHiddenGroups((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(group.key)) {
              newSet.delete(group.key);
            } else {
              newSet.add(group.key);
            }
            return newSet;
          });
        }}
        sx={{
          '&:not(:last-child)': {
            borderColor: 'divider',
            // borderBottom: '1px solid',
          },
          '&:last-child': {
            borderBottom: 0,
          },
          borderTop: 0,
          borderLeft: 0,
          borderRight: 0,
          boxShadow: 'none',
          marginBottom: 1,
          marginLeft: depth > 0 ? 2 : 0,
          '& .MuiAccordionDetails-root': {
            padding: 0,
          },
          '& .MuiAccordionSummary-content': {
            margin: 0,
          },
          '& .MuiPaper-root': {
            boxShadow: 'none',
          },
        }}
      >
        {shouldShowHeader && (
          <AccordionSummary
            sx={{
              minHeight: 48,
              '&.Mui-expanded': {
                minHeight: 48,
              },
              '& .MuiAccordionSummary-content': {
                marginY: 1,
              },
            }}
            expandIcon={<ExpandMore />}
          >
            <Typography
              variant="subtitle1"
              fontWeight={600}
              color="text.primary"
              textTransform="capitalize"
            >
              {group.groupFriendlyValue
                ? `${formatName(group.groupField)}: ${group.groupFriendlyValue}`
                : 'Ungrouped'}
              {!hiddenGroups.has(group.key)
                ? ''
                : ` (${group.entities.length})`}
            </Typography>
          </AccordionSummary>
        )}
        <AccordionDetails>
          {group.entities.map((project, i) => (
            <React.Fragment key={project.id}>
              {i === 0 && (
                <DroppableList
                  context={{
                    entitiesList: allEntities,
                    entityAfter: project,
                  }}
                />
              )}
              <EntityView
                hideType={
                  hideType || (categorizeBy?.current?.includes('type') ?? false)
                }
                {...entityViewProps}
                entity={project}
              />
              <DroppableList
                context={{
                  entitiesList: allEntities,
                  entityBefore: project,
                  entityAfter: group.entities[i + 1]
                    ? group.entities[i + 1]
                    : undefined,
                }}
              />
            </React.Fragment>
          ))}
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Stack gap={1}>
      {(categorizeBy?.current?.length || sortBy?.current) && (
        <Stack direction="row" gap={1} flexWrap="wrap">
          {categorizeBy?.current && categorizeBy.current.length > 0 && (
            <Chip
              label={
                <Stack direction="row" gap={0.5} alignItems="center">
                  {sortCatAsc ? <ArrowDownward /> : <ArrowUpward />}
                  <span>
                    Categorized:{' '}
                    {categorizeBy.current
                      .map((cat) => (cat === 'type' ? 'Type' : formatName(cat)))
                      .join(', ')}
                  </span>
                </Stack>
              }
              onDelete={() => categorizeBy.onChange([])}
              onClick={() => setSortCatAsc((prev) => !prev)}
            />
          )}
          {sortBy?.current && (
            <Chip
              label={
                <Stack direction="row" gap={0.5} alignItems="center">
                  {sortAsc ? <ArrowDownward /> : <ArrowUpward />}
                  <span>Sorted: {formatName(sortBy.current)}</span>
                </Stack>
              }
              onDelete={() => sortBy.onChange(null)}
              onClick={() => setSortAsc((prev) => !prev)}
            />
          )}
        </Stack>
      )}
      <List>
        {entityGroups.length === 0
          ? // No grouping - render entities directly
            (entities || []).map((project, i) => (
              <React.Fragment key={project.id}>
                {i === 0 && (
                  <DroppableList
                    context={{
                      entitiesList: entities ?? [],
                      entityAfter: project,
                    }}
                  />
                )}
                <EntityView
                  hideType={hideType}
                  {...entityViewProps}
                  entity={project}
                />
                <DroppableList
                  context={{
                    entitiesList: entities ?? [],
                    entityBefore: project,
                    entityAfter: entities?.[i + 1]
                      ? entities[i + 1]
                      : undefined,
                  }}
                />
              </React.Fragment>
            ))
          : entityGroups.map((group) => renderGroup(group, entities || []))}
      </List>
    </Stack>
  );
}
